import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Loader2 } from "lucide-react";

import { api, apiError } from "@/lib/api";

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "•";

export default function PublicWall() {
  const { wall } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/public/walls/${wall}`);
      setData(res.data);
    } catch (e) {
      setError(apiError(e));
      setData(false);
    }
  }, [wall]);

  useEffect(() => { load(); }, [load]);

  if (data === null) {
    return <Shell><div className="flex items-center gap-2 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></Shell>;
  }
  if (data === false) {
    return <Shell><p className="text-[13px] text-muted-foreground"
                     data-testid="wall-unavailable">{error}</p></Shell>;
  }

  return (
    <Shell>
      <div className="mx-auto w-full max-w-5xl px-5 py-10" data-testid={`wall-${wall}`}>
        <p className="text-[10px] uppercase tracking-[1.8px] text-muted-foreground">
          {data.organisation.name}
        </p>
        <h1 className="mt-1 font-display text-4xl font-bold text-foreground sm:text-5xl"
            data-testid="wall-title">{data.title}</h1>
        {data.intro && (
          <p className="mt-3 max-w-2xl text-base text-muted-foreground" data-testid="wall-intro">
            {data.intro}
          </p>
        )}

        {data.people.length === 0 ? (
          <p className="mt-10 text-[13px] text-muted-foreground" data-testid="wall-empty">
            Nobody is on this wall yet.
          </p>
        ) : (
          <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.people.map((person, i) => (
              <article key={`${person.name}-${i}`} data-testid={`wall-card-${i}`}
                       className="rounded-[14px] border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5"
                       style={{ animation: `wallIn 420ms ease ${i * 45}ms both` }}>
                <div className="flex items-center gap-3">
                  {person.photo_url
                    ? <img alt="" data-testid={`wall-photo-${i}`}
                           src={`${BACKEND_URL}${person.photo_url}`}
                           className="h-11 w-11 rounded-full object-cover" />
                    : <span className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 font-mono text-[13px] font-semibold text-primary">
                        {initials(person.name)}
                      </span>}
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground">{person.name}</div>
                    <div className="truncate text-[11.5px] capitalize text-muted-foreground">
                      {person.role || person.office}
                    </div>
                  </div>
                </div>
                {person.bio && (
                  <p className="mt-3 text-[12.5px] leading-relaxed text-muted-foreground">
                    {person.bio}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {person.milestone > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-mono text-[10px] text-primary">
                      <Award className="h-3 w-3" /> {person.milestone}-hour milestone
                    </span>
                  )}
                  {typeof person.hours === "number" && person.hours > 0 && (
                    <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {person.hours} hours given
                    </span>
                  )}
                  {person.since && (
                    <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                      since {person.since}
                    </span>
                  )}
                  {(person.skills || []).slice(0, 3).map((s) => (
                    <span key={s}
                          className="rounded-full bg-muted px-2 py-0.5 text-[10px] capitalize text-muted-foreground">
                      {s}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <style>{"@keyframes wallIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}"}</style>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background">{children}</div>
);
